import { Injectable, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";

const UNAUTHORIZED_PATH = "/pages/unauthorized";

/**
 * Tracks whether the most recent navigation outcome was a redirect to the
 * unauthorized page caused by the permission route guard.
 *
 * Purpose (item 1B): prevent an indefinite unauthorized → back → unauthorized
 * loop. When the user lands on the unauthorized page and `goBack()` would send
 * them to a path the guard blocks again, the unauthorized page falls back to
 * the dashboard instead.
 *
 * Mechanism: the guard calls `markBlocked()` right before redirecting to the
 * unauthorized page. A single `NavigationEnd` subscription clears the blocked
 * flag whenever a NON-unauthorized URL successfully activates (a normal,
 * allowed navigation). If `markBlocked()` fires while the flag is still set —
 * meaning we bounced from the unauthorized page straight into another blocked
 * path with no successful navigation in between — `shouldFallbackToDashboard()`
 * returns true.
 */
@Injectable({ providedIn: "root" })
export class UnauthorizedRedirectService {
  private router = inject(Router);

  /** True once the guard has blocked a path; cleared on any allowed nav. */
  private blocked = false;
  /** True when a block fired while already blocked (a redirect loop). */
  private looped = false;

  constructor() {
    // Clear the blocked flag on any successful navigation that is NOT the
    // unauthorized page itself. This is the "allowed navigation in between"
    // signal that breaks the loop detection.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = (e.urlAfterRedirects || e.url || "").split("?")[0];
        if (url !== UNAUTHORIZED_PATH) {
          this.blocked = false;
          this.looped = false;
        }
      });
  }

  /**
   * Called by the permission route guard immediately before redirecting to the
   * unauthorized page. If a block is already pending (no allowed navigation has
   * cleared it), this is a redirect loop.
   */
  markBlocked(): void {
    if (this.blocked) {
      this.looped = true;
    }
    this.blocked = true;
  }

  /**
   * Whether the unauthorized page should fall back to the dashboard instead of
   * navigating back — true when a redirect loop was detected.
   */
  shouldFallbackToDashboard(): boolean {
    return this.looped;
  }
}
